import { loadStripe, Stripe, StripeElementLocale } from '@stripe/stripe-js'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useConfig from 'modules/config/state/use-config'

// Load Stripe into global state to avoid double loading
let stripePromise: Promise<Stripe | null> | undefined

const SUPPORTED_STRIPE_LOCALES: StripeElementLocale[] = [
  'auto',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'es',
  'es-ES',
  'fi',
  'fr',
  'hu',
  'it',
  'ja',
  'lt',
  'lv',
  'nb',
  'nl',
  'pl',
  'pt',
  'ro',
  'ru',
  'sk',
  'sl',
  'sv',
  'th',
  'tr',
  'zh',
]

// Utility function to map Next.js locales to Stripe-supported locales
const mapNextLocaleToStripeLocale = (nextLocale: string | undefined): StripeElementLocale => {
  if (!nextLocale) return 'auto'

  const localeBase = nextLocale.split('-')[0].toLowerCase() // Extract base language code

  return (
    SUPPORTED_STRIPE_LOCALES.includes(localeBase as StripeElementLocale) ? localeBase : 'auto'
  ) as StripeElementLocale
}

// Initialize or get singleton instance of the Stripe SDK
const getStripePromise = async (
  stripePublishableKey: string,
  locale?: string | undefined,
): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey, {
      betas: ['payment_element_disable_card_country_zip_validations_beta_1'],
      locale: mapNextLocaleToStripeLocale(locale),
    })
  }

  // Stripe libraries expect a "MaybeStripe" object which is Promise<Stripe | null>

  // Promise<undefined> can cause a validation error.

  if (stripePromise) return stripePromise

  return null
}

// Alternate hook version of getStripePromise
// React Stripe libraries are a bit picky. They don't like it when different objects

// are passed in even if it's two Promises that resolve to the same object.
export const useMaybeStripe = () => {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const config = useConfig()
  const { i18n } = useTranslation()

  if (!stripe && config.stripePublishableKey) {
    getStripePromise(config.stripePublishableKey, i18n.language)
      .then((actuallyStripe) => {
        if (!stripe) setStripe(actuallyStripe)
      })
      .catch((error) => {
        console.error('Error loading Stripe:', error)
      })
  }

  return stripe
}

export default getStripePromise
